import { KeyboardMouse } from './keyboard-mouse';
import { GamepadSource } from './gamepad';
import { TouchState } from './touch';
import { setActiveDevice } from './active-device';
import { emptyInput, type InputDevice, type InputState } from './types';

/**
 * Merges input sources; the most recently active device wins.
 * Mouse aim needs the player's screen position to become a world-space
 * direction — provided by Game on every poll. Touch source arrives in M4.
 */
export class InputManager {
  private km = new KeyboardMouse();
  private gp = new GamepadSource();
  /** Written by ui/touch-ui.ts */
  readonly touch = new TouchState();
  private state: InputState = emptyInput();
  activeDevice: InputDevice = 'keyboard';

  attach(target: HTMLElement): void {
    this.km.attach(target);
  }

  /** Gamepad rumble passthrough (no-op on other devices) */
  rumble(strong: number, weak: number, durationMs: number): void {
    this.gp.rumble(strong, weak, durationMs);
  }

  /** Coarse-pointer hardware (phones/tablets) — synthetic mouse events on
   * these used to flip us into keyboard mode and hide the touch UI (O2) */
  /** Xbox Edge: the emulated mouse must never win over the real pad */
  private readonly isXbox: boolean =
    typeof navigator !== 'undefined' && /Xbox/i.test(navigator.userAgent);

  readonly coarsePointer: boolean =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window);

  poll(playerScreenX: number, playerScreenY: number): InputState {
    const gpState = this.gp.read();

    const newest = Math.max(this.km.lastActivity, this.gp.lastActivity, this.touch.lastActivity);
    if (this.isXbox && gpState) {
      this.activeDevice = 'gamepad';
    } else if (this.coarsePointer && !gpState) {
      // Phones: mouse emulation never wins — but a CONNECTED pad (Xbox Edge
      // reports coarse pointers too!) always takes normal arbitration
      this.activeDevice = 'touch';
    } else if (newest === this.touch.lastActivity && this.touch.lastActivity > 0) {
      this.activeDevice = 'touch';
    } else if (newest === this.gp.lastActivity && gpState) {
      this.activeDevice = 'gamepad';
    } else {
      this.activeDevice = 'keyboard';
    }
    setActiveDevice(this.activeDevice);

    const s = this.state;

    if (this.activeDevice === 'touch') {
      // Touch: steer + dash/super only; aim & fire are automatic (docs/06)
      s.moveX = this.touch.moveX;
      s.moveY = this.touch.moveY;
      s.hasAim = false;
      s.fire = true;
      s.supershot = this.touch.superHeld;
      s.dashPressed = this.touch.consumeDash();
      s.confirmHeld = false;
      s.dodgeLeftPressed = false;
      s.dodgeRightPressed = false;
      s.altPressed = false;
      s.trashPressed = false;
      s.infoPressed = false;
      s.slowHeld = false;
      s.pausePressed = this.touch.consumePause();
      this.km.endFrame();
      return s;
    }

    if (this.activeDevice === 'gamepad' && gpState) {
      s.moveX = gpState.moveX;
      s.moveY = gpState.moveY;
      const aimLen = Math.hypot(gpState.aimX, gpState.aimY);
      s.hasAim = aimLen > 0.3;
      if (s.hasAim) {
        s.aimX = gpState.aimX / aimLen;
        s.aimY = gpState.aimY / aimLen;
      }
      s.fire = gpState.fire;
      s.supershot = gpState.supershot;
      s.dashPressed = gpState.dashPressed;
      s.confirmHeld = gpState.confirmHeld;
      s.dodgeLeftPressed = gpState.dodgeLeftPressed;
      s.dodgeRightPressed = gpState.dodgeRightPressed;
      s.altPressed = gpState.altPressed;
      s.trashPressed = gpState.trashPressed;
      s.infoPressed = gpState.infoPressed;
      s.slowHeld = gpState.slowHeld;
      s.pausePressed = gpState.pausePressed;
    } else {
      const mv = this.km.moveVector();
      s.moveX = mv.x;
      s.moveY = mv.y;
      const dx = this.km.mouseX - playerScreenX;
      const dy = this.km.mouseY - playerScreenY;
      const len = Math.hypot(dx, dy);
      s.hasAim = len > 4;
      if (s.hasAim) {
        s.aimX = dx / len;
        s.aimY = dy / len;
      }
      s.fire = this.km.mouseLeft;
      s.supershot = this.km.mouseRight || this.km.down('ShiftLeft');
      s.dashPressed = this.km.pressed('Space');
      s.confirmHeld = this.km.down('Space');
      s.dodgeLeftPressed = this.km.pressed('KeyQ');
      s.dodgeRightPressed = this.km.pressed('KeyE');
      s.altPressed = this.km.pressed('KeyR');
      s.trashPressed = this.km.pressed('KeyX');
      s.infoPressed = this.km.pressed('KeyI');
      s.slowHeld = false;
      s.pausePressed = this.km.pressed('Escape');
    }

    this.km.endFrame();
    return s;
  }
}
